import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  setTimerConfig,
  setLoading,
  setError,
  TimerConfig,
} from '../slices/timerConfigurationSlice'
import { sendTimerMessage } from './timerWebsocket'
import {
  fetchTimerConfigurationAPI,
  saveTimerConfigurationAPI,
} from '@/api/timerConfiguration'

export const fetchTimerConfigurationThunk = createAsyncThunk(
  'timerConfiguration/fetchConfiguration',
  async (roomId: string, { dispatch }) => {
    try {
      dispatch(setLoading(true))

      const success = sendTimerMessage({
        type: 'get_timer_config',
        room_id: roomId,
      })

      if (!success) {
        throw new Error('Failed to request timer configuration')
      }

      dispatch(setLoading(false))
      return null
    } catch (error) {
      dispatch(setLoading(false))
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch timer configuration'
      dispatch(setError(errorMessage))
      throw error
    }
  }
)

export const saveTimerConfigurationThunk = createAsyncThunk(
  'timerConfiguration/saveConfiguration',
  async (
    { roomId, config }: { roomId: string; config: TimerConfig },
    { dispatch }
  ) => {
    try {
      dispatch(setLoading(true))

      await saveTimerConfigurationAPI(roomId, config)

      dispatch(setTimerConfig(config))

      dispatch(fetchTimerStatusThunk(roomId))

      dispatch(setLoading(false))
      return config
    } catch (error) {
      dispatch(setLoading(false))
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to save timer configuration'
      dispatch(setError(errorMessage))
      throw error
    }
  }
)

export const fetchTimerStatusThunk = createAsyncThunk(
  'timerConfiguration/fetchStatus',
  async (roomId: string, { dispatch }) => {
    try {
      dispatch(setLoading(true))

      const success = sendTimerMessage({
        type: 'get_timer_status',
        room_id: roomId,
      })

      if (!success) {
        throw new Error('Failed to request timer status')
      }

      dispatch(setLoading(false))
      return null
    } catch (error) {
      dispatch(setLoading(false))
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch timer status'
      dispatch(setError(errorMessage))
      throw error
    }
  }
)

export const startNextRoundThunk = createAsyncThunk(
  'timerConfiguration/startNextRound',
  async (roomId: string, { dispatch }) => {
    try {
      dispatch(setLoading(true))

      const success = sendTimerMessage({
        type: 'start_next_round',
        room_id: roomId,
      })

      if (!success) {
        throw new Error('Failed to send start next round message')
      }

      dispatch(setLoading(false))
      return { success: true }
    } catch (error) {
      dispatch(setLoading(false))
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to start next round'
      dispatch(setError(errorMessage))
      throw error
    }
  }
)
export const endCurrentRoundThunk = createAsyncThunk(
  'timerConfiguration/endCurrentRound',
  async (roundId: number, { dispatch }) => {
    try {
      dispatch(setLoading(true))

      const success = sendTimerMessage({
        type: 'end_round',
        round_id: roundId,
      })

      if (!success) {
        throw new Error('Failed to send end current round message')
      }

      dispatch(setLoading(false))
      return { success: true }
    } catch (error) {
      dispatch(setLoading(false))
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to end current round'
      dispatch(setError(errorMessage))
      throw error
    }
  }
)

export const endGame = createAsyncThunk(
  'timerConfiguration/endGame',
  async (_, { dispatch }) => {
    try {
      dispatch(setLoading(true))

      const success = sendTimerMessage({
        type: 'end_game',
      })

      if (!success) {
        throw new Error('Failed to send end game message')
      }

      dispatch(setLoading(false))
      return { success: true }
    } catch (error) {
      dispatch(setLoading(false))
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to end game'
      dispatch(setError(errorMessage))
      throw error
    }
  }
)

export const endSimulationThunk = createAsyncThunk(
  'timerConfiguration/endSimulation',
  async (_, { dispatch }) => {
    try {
      dispatch(setLoading(true))

      const success = sendTimerMessage({
        type: 'end_simulation',
      })

      if (!success) {
        throw new Error('Failed to send end simulation message')
      }

      dispatch(setLoading(false))
      return { success: true }
    } catch (error) {
      dispatch(setLoading(false))
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to end simulation'
      dispatch(setError(errorMessage))
      throw error
    }
  }
)

export const endCurrentPhaseThunk = createAsyncThunk(
  'timerConfiguration/endCurrentPhase',
  async (phaseId: number, { dispatch }) => {
    try {
      dispatch(setLoading(true))

      const success = sendTimerMessage({
        type: 'end_phase',
        phase_id: phaseId,
      })

      if (!success) {
        throw new Error('Failed to send end current phase message')
      }

      dispatch(setLoading(false))
      return { success: true }
    } catch (error) {
      dispatch(setLoading(false))
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to end current phase'
      dispatch(setError(errorMessage))
      throw error
    }
  }
)

export const previousPhaseThunk = createAsyncThunk(
  'timerConfiguration/previousPhase',
  async (roomId: string, { dispatch }) => {
    try {
      dispatch(setLoading(true))

      const success = sendTimerMessage({
        type: 'previous_phase',
        room_id: roomId,
      })

      if (!success) {
        throw new Error('Failed to send previous phase message')
      }

      dispatch(setLoading(false))
      return { success: true }
    } catch (error) {
      dispatch(setLoading(false))
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to go to previous phase'
      dispatch(setError(errorMessage))
      throw error
    }
  }
)

export const pauseGameThunk = createAsyncThunk(
  'timerConfiguration/pauseGame',
  async (_, { dispatch }) => {
    try {
      dispatch(setLoading(true))

      const success = sendTimerMessage({
        type: 'pause_game',
      })

      if (!success) {
        throw new Error('Failed to send pause game message')
      }

      dispatch(setLoading(false))
      return { success: true }
    } catch (error) {
      dispatch(setLoading(false))
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to pause the game'
      dispatch(setError(errorMessage))
      throw error
    }
  }
)

export const resumeGameThunk = createAsyncThunk(
  'timerConfiguration/resumeGame',
  async (_, { dispatch }) => {
    try {
      dispatch(setLoading(true))

      const success = sendTimerMessage({
        type: 'resume_game',
      })

      if (!success) {
        throw new Error('Failed to send resume game message')
      }

      dispatch(setLoading(false))
      return { success: true }
    } catch (error) {
      dispatch(setLoading(false))
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to resume the game'
      dispatch(setError(errorMessage))
      throw error
    }
  }
)

export const startPhaseThunk = createAsyncThunk(
  'timerConfiguration/startPhase',
  async (
    {
      roomId,
      phaseId,
      duration,
    }: { roomId: string; phaseId: number; duration: number },
    { dispatch }
  ) => {
    try {
      dispatch(setLoading(true))

      const success = sendTimerMessage({
        type: 'start_phase',
        room_id: roomId,
        phase_id: phaseId,
        duration,
      })

      if (!success) {
        throw new Error('Failed to send start phase message')
      }

      dispatch(setLoading(false))
      return { success: true }
    } catch (error) {
      dispatch(setLoading(false))
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to start phase'
      dispatch(setError(errorMessage))
      throw error
    }
  }
)

export const fetchTimerConfigurationAPIThunk = createAsyncThunk(
  'timerConfiguration/fetchConfigurationAPI',
  async (roomId: string, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      const config = await fetchTimerConfigurationAPI(roomId)

      dispatch(setTimerConfig(config))
      dispatch(setLoading(false))
      return config
    } catch (error) {
      dispatch(setLoading(false))
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to fetch timer configuration'
      dispatch(setError(errorMessage))
      throw error
    }
  }
)
